import {
  inheritRequestCorrelation,
  StructuredLogger,
  reportContractMismatch,
} from '@brighthub/structured-logging';
import { Injectable, inject } from '@angular/core';
import { HttpErrorResponse } from '@angular/common/http';
import { Observable, catchError, from, of, switchMap, throwError } from 'rxjs';
import {
  ApiService,
  AwsApiWrapperService,
  GenericResponseCode,
  GenericResponseResult,
} from '@brighthub/api';
import type { FinancialSnapshotApi } from '../financial-snapshot-api.token';
import { FINANCIAL_SNAPSHOT_API_CONFIG } from '../config/financial-snapshot-api.config';
import type {
  FinancialSnapshotLoadResult,
  FinancialSnapshotPatchBody,
  FinancialSnapshotPostBody,
  FinancialSnapshotResponse,
  FinancialSnapshotTemplate,
} from '../financial-snapshot.model';
import {
  isFinancialSnapshotPayload,
  isFinancialSnapshotTemplate,
  normalizeFinancialSnapshotResponse,
} from './financial-snapshot-response.guard';

type Envelope<T> = GenericResponseResult & { payload?: T };

/**
 * Validation failures carry the useful text in `payload.message` and leave
 * `result.message` empty (e.g. "At least one subCategory is expected in Category x").
 */
function envelopeErrorMessage(res: unknown): string | null {
  if (!res || typeof res !== 'object') {
    return null;
  }
  const result = (res as { result?: { message?: unknown } }).result;
  if (typeof result?.message === 'string' && result.message.trim()) {
    return result.message;
  }
  const payload = (res as { payload?: { message?: unknown } }).payload;
  if (typeof payload?.message === 'string' && payload.message.trim()) {
    return payload.message;
  }
  return null;
}

/**
 * Live Financial Snapshot API via `api-internal`.
 * Unwraps `{ result, payload }`, normalizes read model, and re-GETs after writes.
 */
@Injectable()
export class HttpFinancialSnapshotApiService implements FinancialSnapshotApi {
  private readonly _api = inject(ApiService);
  private readonly _logger = inject(StructuredLogger);
  private readonly _wrapper = inject(AwsApiWrapperService);
  private readonly _apiName = FINANCIAL_SNAPSHOT_API_CONFIG.apiName;
  private readonly _pathPrefix = FINANCIAL_SNAPSHOT_API_CONFIG.pathPrefix;

  /** Cached template for normalize / post-write re-GET. */
  private _template: FinancialSnapshotTemplate | null = null;

  getTemplate(): Observable<FinancialSnapshotTemplate> {
    return this._api
      .get<Envelope<unknown>>(this._apiName, `${this._pathPrefix}/template`)
      .pipe(
        switchMap((res) => {
          this._assertOk(res);
          const payload = res.payload;
          if (!isFinancialSnapshotTemplate(payload)) {
            reportContractMismatch(this._logger, 'financial_snapshot.template');
            return throwError(
              () =>
                new Error(
                  'Financial Snapshot template response failed type-guard',
                ),
            );
          }
          this._template = payload;
          return of(payload);
        }),
      );
  }

  getSnapshot(guid: string): Observable<FinancialSnapshotLoadResult> {
    // Use wrapper directly so HTTP 204 / null bodies map to empty without ApiService
    // choking on a missing `result` envelope.
    return from(
      this._wrapper.get(this._apiName, this._pathPrefix, {
        queryParams: { guid },
      }),
    ).pipe(
      switchMap((raw) => {
        if (raw === false) {
          return throwError(
            () =>
              new Error(
                'Not signed in yet — no access token available for the request.',
              ),
          );
        }
        if (raw == null || raw === '') {
          return of({ kind: 'empty' as const });
        }
        try {
          return of(this._mapGetSnapshot(raw as Envelope<unknown>));
        } catch (err) {
          return throwError(() => err);
        }
      }),
      catchError((err: unknown) => {
        // EDD: only HTTP 204 (and empty envelopes) mean "no snapshot yet".
        // 404 is treated as a real load failure (bad guid / routing / permissions).
        if (err instanceof HttpErrorResponse && err.status === 204) {
          return of({ kind: 'empty' as const });
        }
        const body = err instanceof HttpErrorResponse ? err.error : null;
        if (
          body &&
          typeof body === 'object' &&
          ((body as { statusCode?: number }).statusCode === 204 ||
            (body as { result?: { status?: number } }).result?.status === 204)
        ) {
          return of({ kind: 'empty' as const });
        }
        return throwError(() => err);
      }),
    );
  }

  postSnapshot(
    body: FinancialSnapshotPostBody,
  ): Observable<FinancialSnapshotResponse> {
    // `ApiService` wraps its `init` argument as the request body, so pass the payload
    // directly — wrapping it again sends `{ body: { … } }` and the lambda 400s.
    const categoryKeyCount = body.categories
      ? Object.keys(body.categories).length
      : 0;
    return this._api
      .post<Envelope<unknown>>(this._apiName, this._pathPrefix, body, {
        suppressRequestBodyTelemetry: true,
        telemetryMetadata: { categoryKeyCount },
      })
      .pipe(
        switchMap((res) => {
          this._assertWriteOk(res);
          return this._reloadSnapshot(body.guid);
        }),
        catchError((err: unknown) => this._mapWriteError(err)),
      );
  }

  inviteClient(guid: string): Observable<void> {
    return this._api
      .post<Envelope<unknown>>(
        this._apiName,
        `${this._pathPrefix}/invite`,
        { guid },
        {
          suppressRequestBodyTelemetry: true,
        },
      )
      .pipe(
        switchMap((res) => {
          this._assertWriteOk(res);
          return of(undefined);
        }),
        catchError((err: unknown) => this._mapWriteError(err)),
      );
  }

  patchCategory(
    guid: string,
    body: FinancialSnapshotPatchBody,
  ): Observable<FinancialSnapshotResponse> {
    const patchBody = { guid, ...body };
    const categoryKeyCount = Object.keys(body).length;
    return this._api
      .patch<Envelope<unknown>>(this._apiName, this._pathPrefix, patchBody, {
        suppressRequestBodyTelemetry: true,
        telemetryMetadata: { categoryKeyCount },
      })
      .pipe(
        switchMap((res) => {
          this._assertWriteOk(res);
          return this._reloadSnapshot(guid);
        }),
        catchError((err: unknown) => this._mapWriteError(err)),
      );
  }

  /** Keep the lambda's validation text when a write fails with a real HTTP error. */
  private _mapWriteError(err: unknown): Observable<never> {
    if (err instanceof HttpErrorResponse) {
      const message = envelopeErrorMessage(err.error);
      if (message) {
        return throwError(() =>
          inheritRequestCorrelation(new Error(message), err),
        );
      }
    }
    return throwError(() => err);
  }

  private _reloadSnapshot(guid: string): Observable<FinancialSnapshotResponse> {
    return this.getSnapshot(guid).pipe(
      switchMap((result) => {
        if (result.kind === 'empty') {
          return throwError(() => new Error('Snapshot missing after write'));
        }
        return of(result.snapshot);
      }),
    );
  }

  private _mapGetSnapshot(res: Envelope<unknown>): FinancialSnapshotLoadResult {
    if (res == null || typeof res !== 'object' || Array.isArray(res)) {
      reportContractMismatch(this._logger, 'financial_snapshot.snapshot');
      throw new Error('Financial Snapshot GET response failed type-guard');
    }
    if (this._isEmptyEnvelope(res)) {
      return { kind: 'empty' };
    }
    // Legacy mobile-shaped body (document at root, no envelope).
    if (!res.result && isFinancialSnapshotPayload(res)) {
      const template = this._template ?? {};
      return {
        kind: 'snapshot',
        snapshot: normalizeFinancialSnapshotResponse(res, template),
      };
    }
    this._assertOk(res);
    const payload = res.payload;
    if (payload == null || payload === '') {
      return { kind: 'empty' };
    }
    if (!isFinancialSnapshotPayload(payload)) {
      reportContractMismatch(this._logger, 'financial_snapshot.snapshot');
      throw new Error('Financial Snapshot GET response failed type-guard');
    }
    const template = this._template ?? {};
    return {
      kind: 'snapshot',
      snapshot: normalizeFinancialSnapshotResponse(payload, template),
    };
  }

  private _isEmptyEnvelope(res: Envelope<unknown>): boolean {
    const statusCode = (res as { statusCode?: number }).statusCode;
    if (statusCode === 204) {
      return true;
    }
    if (res.result?.status === 204) {
      return true;
    }
    return false;
  }

  private _assertOk(res: Envelope<unknown> | null | undefined): void {
    if (!res?.result) {
      throw new Error('Financial Snapshot API returned an empty response');
    }
    if (
      res.result.code !== GenericResponseCode.SERVICE_OK ||
      res.result.status !== 200
    ) {
      throw inheritRequestCorrelation(
        new Error(
          envelopeErrorMessage(res) ??
            `Financial Snapshot API error: ${res.result.code}`,
        ),
        res,
      );
    }
  }

  private _assertWriteOk(res: unknown): void {
    if (res == null) {
      throw new Error('Financial Snapshot write returned an empty response');
    }
    if (typeof res === 'object' && res !== null && 'result' in res) {
      this._assertOk(res as Envelope<unknown>);
      return;
    }
    const record = res as { statusCode?: number; status?: number };
    if (record.statusCode === 200 || record.status === 200) {
      return;
    }
    throw new Error('Financial Snapshot write failed');
  }
}
